import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { branchHoursLiveHint, parseFirstDailyTimeRangeMinutes } from '../widget-lib/branch-hours-status';
import {
  EMBED_BRANCH_PREFS_CHANGED_EVENT,
  loadBranchFilterPrefs,
  toggleDefaultScheduleBranch,
} from '../widget-lib/embed-user-prefs';
import { formatPhone, telHref } from '../widget-lib/phone-tel';
import { yandexMapsSearchUrl } from '../widget-lib/yandex-maps-url';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { PATH } from '../widget-lib/path-routes';
import { BranchPickLink } from './branch-pick-link';

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" fill="currentColor"/>
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011-.27 11.36 11.36 0 003.59.57 1 1 0 011 1V20a1 1 0 01-1 1A16 16 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.59 1 1 0 01-.27 1z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75"/>
      <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function PaperclipIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 16.5 7.181l-7.693 7.693a2.25 2.25 0 0 1-3.182-3.182l7.693-7.693" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const pickNameTextClass =
  'text-base font-bold leading-snug text-[var(--text)] sm:text-lg hover:text-[var(--accent)] transition-colors';

export type BranchDetailCardProps = {
  branchId: number;
  name: string;
  address: string | null;
  workingHours: string | null;
  comment?: string | null;
  phone?: string | null;
  organizationId?: number | null;
  timezone?: string | null;
  showDefaultBranchToggle?: boolean;
  pickBranch?: boolean;
  footer?: ReactNode;
};

export function BranchDetailCard({
  branchId,
  name,
  address,
  workingHours,
  comment,
  phone,
  organizationId,
  timezone,
  showDefaultBranchToggle = false,
  pickBranch = true,
  footer,
}: BranchDetailCardProps) {
  const suffix = useQuerySuffix();
  const addr = address?.trim() ?? '';
  const mapHref = addr ? yandexMapsSearchUrl(addr) : null;
  const hours = workingHours?.trim();
  const note = comment?.trim();
  const tel = phone?.trim();
  const tz = (timezone ?? '').trim() || 'Europe/Moscow';

  const [defaultPinned, setDefaultPinned] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    function syncPin() {
      if (organizationId == null || organizationId <= 0) {
        setDefaultPinned(false);
        return;
      }
      const p = loadBranchFilterPrefs(organizationId);
      setDefaultPinned(p?.defaultBranchId === branchId);
    }
    syncPin();
    window.addEventListener(EMBED_BRANCH_PREFS_CHANGED_EVENT, syncPin);
    return () => window.removeEventListener(EMBED_BRANCH_PREFS_CHANGED_EVENT, syncPin);
  }, [organizationId, branchId]);

  useEffect(() => {
    const id = window.setInterval(() => setTick((x) => x + 1), 60000);
    return () => window.clearInterval(id);
  }, []);

  const hint = branchHoursLiveHint(workingHours, tz, new Date());
  const range = hours ? parseFirstDailyTimeRangeMinutes(hours) : null;
  void tick;

  const isOpen = hint?.kind === 'open' || hint?.kind === 'closes_in';
  const hintText =
    hint?.kind === 'closes_in'
      ? `Закроется через ${hint.minutes} мин.`
      : hint?.kind === 'open'
        ? 'Открыто'
        : range
          ? 'Закрыто'
          : null;

  function onToggleDefault(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (organizationId == null || organizationId <= 0) return;
    toggleDefaultScheduleBranch(organizationId, branchId);
  }

  function onPickClick(e: MouseEvent) {
    if (!pickBranch) e.preventDefault();
  }

  return (
    <li className="list-none">
      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-[var(--shadow-card)] transition-shadow duration-200 hover:shadow-md">
        <div className="p-4 sm:p-5">
          <div className="flex min-w-0 items-start gap-3">
            <div className="min-w-0 flex-1">
              {pickBranch ? (
                <BranchPickLink
                  branchId={branchId}
                  variant="text"
                  className={pickNameTextClass}
                  organizationId={organizationId}
                >
                  <span className="capitalize">{name}</span>
                </BranchPickLink>
              ) : (
                <p className="text-base font-bold leading-snug text-[var(--text)] sm:text-lg"><span className="capitalize">{name}</span></p>
              )}
            </div>
            {showDefaultBranchToggle && organizationId != null && organizationId > 0 ? (
              <button
                type="button"
                onClick={onToggleDefault}
                className={
                  defaultPinned
                    ? 'shrink-0 rounded-lg p-2 text-[var(--accent)] hover:bg-[var(--bg-soft)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]'
                    : 'shrink-0 rounded-lg p-2 text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--accent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]'
                }
                title={defaultPinned ? 'Убрать филиал по умолчанию' : 'Сделать филиалом по умолчанию'}
                aria-pressed={defaultPinned}
                aria-label={defaultPinned ? 'Снять филиал по умолчанию' : 'Назначить филиал по умолчанию'}
              >
                <PaperclipIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="mt-3 space-y-2.5">
            {mapHref ? (
              <a
                href={mapHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2.5 text-[13px] leading-snug text-[var(--text)] transition-colors hover:text-[var(--accent)] group"
              >
                <MapPinIcon className="mt-0.5 h-[16px] w-[16px] shrink-0 text-[var(--accent)]" />
                <span className="min-w-0 flex-1 underline-offset-2 group-hover:underline">{addr}</span>
              </a>
            ) : (
              <div className="flex items-start gap-2.5 text-[13px] text-[var(--text-muted)]">
                <MapPinIcon className="mt-0.5 h-[16px] w-[16px] shrink-0 text-[var(--text-muted)]" />
                <span>Адрес не указан</span>
              </div>
            )}

            {hours ? (
              <div className="flex items-start gap-2.5 text-[13px] leading-snug text-[var(--text)]">
                <ClockIcon className="mt-0.5 h-[16px] w-[16px] shrink-0 text-[var(--accent)]" />
                <div className="min-w-0 flex-1">
                  <span className="text-[var(--text-muted)]">{hours}</span>
                  {hintText ? (
                    <span className={`ml-2 inline-flex items-center gap-1 text-xs font-medium ${isOpen ? 'text-green-600' : 'text-[var(--text-muted)]'}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${isOpen ? 'bg-green-500' : 'bg-[var(--text-muted)]'}`} />
                      {hintText}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 text-[13px] text-[var(--text-muted)]">
                <ClockIcon className="mt-0.5 h-[16px] w-[16px] shrink-0 text-[var(--text-muted)]" />
                <span>Часы работы не указаны</span>
              </div>
            )}

            {tel ? (
              <a
                href={`tel:${tel.replace(/\s+/g, '')}`}
                className="flex items-start gap-2.5 text-[13px] leading-snug text-[var(--text)] transition-colors hover:text-[var(--accent)] group"
              >
                <PhoneIcon className="mt-0.5 h-[16px] w-[16px] shrink-0 text-[var(--accent)]" />
                <span className="underline-offset-2 group-hover:underline">{formatPhone(tel)}</span>
              </a>
            ) : null}

            {note ? (
              <p className="mt-2 whitespace-pre-wrap text-[13px] leading-snug text-[var(--text-muted)] border-l-2 border-[var(--border)] pl-3">
                {note}
              </p>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {mapHref ? (
              <a
                href={mapHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onPickClick}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-soft)] px-3.5 text-[12px] font-semibold text-[var(--text)] transition-colors hover:bg-[var(--border)] active:scale-[0.98]"
              >
                <MapPinIcon className="h-[14px] w-[14px]" />
                Построить маршрут
              </a>
            ) : null}
            {tel ? (
              <a
                href={telHref(tel)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[var(--accent-bg)] px-3.5 text-[12px] font-semibold text-[var(--accent-fg)] transition-colors hover:bg-[var(--accent-bg-hover)] active:scale-[0.98]"
              >
                <PhoneIcon className="h-[14px] w-[14px]" />
                Позвонить
              </a>
            ) : null}
            {pickBranch ? (
              <Link
                to={`${PATH.schedule}${suffix}&branch_id=${branchId}`}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[var(--accent-bg)] px-3.5 text-[12px] font-semibold text-[var(--accent-fg)] transition-colors hover:bg-[var(--accent-bg-hover)] active:scale-[0.98]"
              >
                Записаться
              </Link>
            ) : null}
          </div>
        </div>
        {footer ? <div className="border-t border-[var(--border)] px-4 py-3 sm:px-5">{footer}</div> : null}
      </div>
    </li>
  );
}
